import React from 'react';
import { Briefcase, Users, Building2 } from 'lucide-react';

/**
 * Componente de 4 tablas compactas para el dashboard de Billing
 * Replica de TablasCompactas.jsx de Timesheets, adaptado para Cloud Unificado
 */
export default function TablasCompactas({
  precioXClienteData = [],
  precioXFabricanteData = [],
  precioXProveedorData = [],
  resumenServicioData = [],
  isDarkMode = true,
  onClienteClick = null,
  onFabricanteClick = null,
  onServicioClick = null,
  onProveedorClick = null,
  selectedClientes = [],
  selectedFabricantes = [],
  selectedServicios = [],
  selectedProveedores = []
}) {
  // Estilos
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg hover:border-emerald-400 transition-all duration-300';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  const tableHeaderBg = isDarkMode ? 'bg-[#00FF66]/8' : 'bg-emerald-50';
  const tableRowHover = isDarkMode ? 'hover:bg-[#00FF66]/5' : 'hover:bg-emerald-50';
  const tableBorder = isDarkMode ? 'border-[#00FF66]/15' : 'border-gray-200';
  const getRowSelectBg = (isSelected) => {
    if (isSelected) {
      return isDarkMode ? 'bg-[#00FF66]/15' : 'bg-emerald-100';
    }
    return '';
  };
  const getIconChipClasses = (color) => {
    if (isDarkMode) {
      return `p-2 rounded-lg border flex items-center justify-center`;
    }
    return 'p-2 rounded-lg border flex items-center justify-center';
  };

  // Función para formatear precio
  const formatPrecio = (precio) => {
    if (!precio && precio !== 0) return '$0.00';
    return '$' + parseFloat(precio).toLocaleString('es-MX', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Componente de tabla genérica
  const TablaCompacta = ({ titulo, Icon, iconColor, columnas, datos, onRowClick = null, selectedValues = [] }) => {
    const iconContainerStyle = isDarkMode
      ? {
          backgroundColor: `${iconColor}26`,
          borderColor: `${iconColor}66`,
          boxShadow: `0 0 12px ${iconColor}33`
        }
      : {
          backgroundColor: `${iconColor}1A`,
          borderColor: `${iconColor}66`
        };

    return (
      <div className={`${cardClasses} p-4 flex flex-col h-full`}>
        {/* Header */}
        <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${tableBorder}`}>
          <div className={getIconChipClasses(iconColor)} style={iconContainerStyle}>
            <Icon className="w-4 h-4" style={{ color: iconColor }} />
          </div>
          <div className="flex-1">
            <h3 className={`${textPrimary} text-sm font-bold`}>{titulo}</h3>
            <p className={`${textMuted} text-xs`}>{datos.length} registros</p>
          </div>
        </div>

        {/* Tabla con scroll */}
        <div
          className="flex-1 overflow-auto [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full"
          style={{
            maxHeight: '400px',
            scrollbarWidth: 'thin',
            scrollbarColor: isDarkMode ? '#00FF66 #062115' : '#10B981 #ECFDF5'
          }}
        >
          <table className="w-full text-xs">
            <thead className={`${tableHeaderBg} sticky top-0 z-10`}>
              <tr>
                {columnas.map((col, idx) => (
                  <th
                    key={idx}
                    className={`px-2 py-2 text-left ${textSecondary} font-semibold border-b ${tableBorder} ${
                      col.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.length === 0 ? (
                <tr>
                  <td colSpan={columnas.length} className={`px-2 py-4 text-center ${textMuted}`}>
                    No hay datos disponibles
                  </td>
                </tr>
              ) : (
                datos.map((fila, idx) => {
                  const primeraClave = columnas[0]?.key;
                  const valorFila = fila[primeraClave];
                  const isSelected = selectedValues.includes(valorFila);
                  
                  return (
                    <tr 
                      key={idx}
                      onClick={() => onRowClick && onRowClick(valorFila)}
                      className={`${tableRowHover} ${getRowSelectBg(isSelected)} transition-colors border-b ${tableBorder} ${
                        onRowClick ? 'cursor-pointer' : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
                          onRowClick(valorFila);
                        }
                      }}
                    >
                      {columnas.map((col, colIdx) => (
                        <td
                          key={colIdx}
                          className={`px-2 py-2 ${textPrimary} ${
                            col.align === 'right' ? 'text-right' : ''
                          } ${col.bold ? 'font-semibold' : ''}`}
                        >
                          {col.render ? col.render(fila) : fila[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
      {/* Tabla 1: Precio por Cliente */}
      <TablaCompacta
        titulo="Precio por Cliente"
        Icon={Briefcase}
        iconColor="#00D4AA"
        columnas={[
          { key: 'cliente', label: 'Cliente', align: 'left' },
          { 
            key: 'precio_neto', 
            label: 'Precio Neto', 
            align: 'right', 
            bold: true,
            render: (fila) => formatPrecio(fila.precio_neto)
          }
        ]}
        datos={precioXClienteData}
        onRowClick={onClienteClick}
        selectedValues={selectedClientes}
      />

      {/* Tabla 2: Precio por Fabricante (categorización de Proveedor) */}
      <TablaCompacta
        titulo="Precio por Fabricante"
        Icon={Users}
        iconColor="#34D399"
        columnas={[
          { key: 'fabricante', label: 'Fabricante', align: 'left' },
          { 
            key: 'precio_neto', 
            label: 'Precio Neto', 
            align: 'right', 
            bold: true,
            render: (fila) => formatPrecio(fila.precio_neto)
          }
        ]}
        datos={precioXFabricanteData}
        onRowClick={onFabricanteClick}
        selectedValues={selectedFabricantes}
      />

      {/* Tabla 3: Precio por Servicio */}
      <TablaCompacta
        titulo="Precio por Servicio"
        Icon={Building2}
        iconColor="#1EEB8A"
        columnas={[
          { key: 'servicio', label: 'Servicio', align: 'left' },
          { 
            key: 'precio_neto', 
            label: 'Precio Neto', 
            align: 'right', 
            bold: true,
            render: (fila) => formatPrecio(fila.precio_neto)
          }
        ]}
        datos={resumenServicioData}
        onRowClick={onServicioClick}
        selectedValues={selectedServicios}
      />

      {/* Tabla 4: Precio por Proveedor (valores específicos) */}
      <TablaCompacta
        titulo="Precio por Proveedor"
        Icon={Building2}
        iconColor="#00FF66"
        columnas={[
          { key: 'proveedor', label: 'Proveedor', align: 'left' },
          { 
            key: 'precio_neto', 
            label: 'Precio Neto', 
            align: 'right', 
            bold: true,
            render: (fila) => formatPrecio(fila.precio_neto)
          }
        ]}
        datos={precioXProveedorData}
        onRowClick={onProveedorClick}
        selectedValues={selectedProveedores}
      />
    </div>
  );
}
